import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import type { SelectOption } from '@/components/common/OptionSelect';
import {
  paginatedPcmpProjectsSchema,
  pcmpCatalogItemSchema,
  pcmpProjectDeleteCheckSchema,
  pcmpProjectDetailSchema,
  pcmpProjectOptionsSchema,
  pcmpProjectRefSchema,
  type PaginatedPcmpProjects,
  type PcmpCatalogItem,
  type PcmpProjectDeleteCheck,
  type PcmpProjectDetail,
  type PcmpProjectListParams,
  type PcmpProjectOptions,
  type PcmpProjectPayload,
  type PcmpProjectRef,
} from '../types/pcmpProject.types';

export const pcmpProjectQueryKeys = {
  all: () => ['pcmp-projects'] as const,
  lists: () => [...pcmpProjectQueryKeys.all(), 'list'] as const,
  listInfinite: (params: Omit<PcmpProjectListParams, 'page'>) =>
    [...pcmpProjectQueryKeys.lists(), 'infinite', params] as const,
  detail: (id: number) => [...pcmpProjectQueryKeys.all(), 'detail', id] as const,
  deleteCheck: (id: number) => [...pcmpProjectQueryKeys.all(), 'delete-check', id] as const,
  options: () => [...pcmpProjectQueryKeys.all(), 'options'] as const,
  picker: () => [...pcmpProjectQueryKeys.lists(), 'picker'] as const,
};

const PCMP_PROJECT_PICKER_LIMIT = 500;

/** Active projects as `{ id, name }`, for a project filter. */
export const pcmpProjectPickerQueryOptions = () =>
  queryOptions({
    queryKey: pcmpProjectQueryKeys.picker(),
    queryFn: async (): Promise<SelectOption[]> => {
      const res = await apiClient.get(API.pcmpProject.list, {
        params: { isActive: true, page: 1, limit: PCMP_PROJECT_PICKER_LIMIT },
      });
      return paginatedPcmpProjectsSchema
        .parse(res.data)
        .data.map((project) => ({ id: project.id, name: project.displayName }));
    },
    staleTime: STALE_TIME,
  });

export const pcmpProjectListInfiniteQueryOptions = (params: Omit<PcmpProjectListParams, 'page'>) =>
  infiniteQueryOptions({
    queryKey: pcmpProjectQueryKeys.listInfinite(params),
    queryFn: async ({ pageParam }): Promise<PaginatedPcmpProjects> => {
      const res = await apiClient.get(API.pcmpProject.list, {
        params: { ...params, page: pageParam },
      });
      return paginatedPcmpProjectsSchema.parse(res.data);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    staleTime: STALE_TIME,
  });

export const pcmpProjectDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: pcmpProjectQueryKeys.detail(id),
    queryFn: async (): Promise<PcmpProjectDetail> => {
      const res = await apiClient.get(API.pcmpProject.detail(id));
      return pcmpProjectDetailSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const pcmpProjectOptionsQueryOptions = () =>
  queryOptions({
    queryKey: pcmpProjectQueryKeys.options(),
    queryFn: async (): Promise<PcmpProjectOptions> => {
      const res = await apiClient.get(API.pcmpProject.options);
      return pcmpProjectOptionsSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

// Always fresh: a submission can arrive between opening the list and deleting.
export const pcmpProjectDeleteCheckQueryOptions = (id: number) =>
  queryOptions({
    queryKey: pcmpProjectQueryKeys.deleteCheck(id),
    queryFn: async (): Promise<PcmpProjectDeleteCheck> => {
      const res = await apiClient.get(API.pcmpProject.deleteCheck(id));
      return pcmpProjectDeleteCheckSchema.parse(res.data);
    },
    staleTime: 0,
  });

export async function setPcmpProjectStatus(id: number, isActive: boolean): Promise<void> {
  await apiClient.patch(API.pcmpProject.status(id), { isActive });
}

export async function deletePcmpProject(id: number): Promise<void> {
  await apiClient.delete(API.pcmpProject.delete(id));
}

export async function createPcmpProject(payload: PcmpProjectPayload): Promise<PcmpProjectRef> {
  const res = await apiClient.post(API.pcmpProject.create, payload);
  return pcmpProjectRefSchema.parse(res.data);
}

/** Sends the whole form; the API diff-syncs the price grid. */
export async function updatePcmpProject(id: number, payload: PcmpProjectPayload): Promise<void> {
  await apiClient.patch(API.pcmpProject.update(id), payload);
}

export async function createPcmpCategory(name: string): Promise<PcmpCatalogItem> {
  const res = await apiClient.post(API.pcmpCategory.create, { name });
  return pcmpCatalogItemSchema.parse(res.data);
}
